import { Controller, type UseFieldArrayReturn } from 'react-hook-form'
import { Moon, Sun, Trash2 } from '@/components/icons'
import { approachTypeOptions, dayNightOptions } from '@@api/flightLog.shared'
import type { DayNight, FlightLogFormData } from '../form'
import type { FlightLogSectionContext } from '../useFlightLogForm'
import { HmmRow, SelectRowField, TextRow } from '../../ui/prntr-fields'
import { PrntrGroup, PrntrPill } from '../../ui/prntr-input'
import { AddPillButton } from '../rows'
import { SegmentedSwitch } from '../SegmentedSwitch'

const approachOptions = approachTypeOptions.map((option) => ({ value: option, label: option }))

export function ConditionsSection({
  context,
  approachArray,
  dayTimeAuto,
  nightTimeAuto,
  takeOffAuto,
  landingAuto,
  instrumentTimeAuto,
  solarAvailable,
}: {
  context: FlightLogSectionContext
  approachArray: UseFieldArrayReturn<FlightLogFormData, 'conditions.approaches'>
  // Whether each value still holds its auto-computed figure (blue Auto pill)
  dayTimeAuto: boolean
  nightTimeAuto: boolean
  takeOffAuto: boolean
  landingAuto: boolean
  instrumentTimeAuto: boolean
  // Day/night could be worked out from the UTC clocks + airport positions
  solarAvailable: boolean
}) {
  const { control, readOnly, fieldError } = context
  return (
    <>
      {(['takeOff', 'landing'] as const).map((phase) => (
        <div key={phase} className="flex flex-col gap-2 py-2">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-content-primary-subtle">
              {phase === 'takeOff' ? 'Take Off' : 'Landing'}
            </p>
            {(phase === 'takeOff' ? takeOffAuto : landingAuto) && (
              <PrntrPill tone="info">Auto</PrntrPill>
            )}
          </div>
          <Controller
            control={control}
            name={`conditions.${phase}`}
            render={({ field }) => (
              <SegmentedSwitch
                options={dayNightOptions}
                value={(field.value as DayNight | null) ?? null}
                onChange={field.onChange}
                icons={{ Day: <Sun />, Night: <Moon /> }}
                readOnly={readOnly}
                error={!!fieldError(`conditions.${phase}`)}
              />
            )}
          />
          {fieldError(`conditions.${phase}`) && (
            <p className="text-xs text-content-negative-bold">{fieldError(`conditions.${phase}`)}</p>
          )}
        </div>
      ))}
      {solarAvailable && (
        <p className="text-xs text-content-primary-subtle">
          Day/night worked out from the UTC times and airport positions (civil twilight).
        </p>
      )}
      <PrntrGroup>
        <HmmRow
          control={control}
          name="conditions.dayTime"
          label="Day Time"
          auto={dayTimeAuto}
          readOnly={readOnly}
          error={fieldError('conditions.dayTime')}
        />
        <HmmRow
          control={control}
          name="conditions.nightTime"
          label="Night Time"
          auto={nightTimeAuto}
          readOnly={readOnly}
          error={fieldError('conditions.nightTime')}
        />
        <HmmRow
          control={control}
          name="conditions.instrumentTime"
          label="Instrument Time"
          auto={instrumentTimeAuto}
          readOnly={readOnly}
          error={fieldError('conditions.instrumentTime')}
        />
      </PrntrGroup>
      {approachArray.fields.map((row, index) => (
        <div key={row.id} className="flex flex-col">
          <div className="flex items-center justify-between pt-3">
            <p className="text-sm font-semibold text-content-primary-bold">Approach {index + 1}</p>
            {!readOnly && (
              <button
                type="button"
                className="flex size-6 items-center justify-center"
                onClick={() => approachArray.remove(index)}
              >
                <Trash2 className="size-4 text-content-primary-subtle" />
              </button>
            )}
          </div>
          <PrntrGroup>
            <SelectRowField
              control={control}
              name={`conditions.approaches.${index}.type`}
              label="Approach Type"
              options={approachOptions}
              readOnly={readOnly}
            />
            <TextRow
              control={control}
              name={`conditions.approaches.${index}.runway`}
              label="Runway"
              placeholder="e.g. 18L"
              readOnly={readOnly}
            />
          </PrntrGroup>
        </div>
      ))}
      {!readOnly && (
        <AddPillButton
          label="Add Approach"
          onClick={() => approachArray.append({ type: 'ILS', runway: '' })}
        />
      )}
    </>
  )
}
